import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import Portal from 'components/Shared/Portal'
import Modal from 'components/Shared/Modal'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import { actions, useConnectContext, useConnectDispatchContext } from './connect'
import integrationAssetsApi from 'domain/integrationAssets/api'

function AssetAvatar({ asset }) {
  const [imgError, setImgError] = useState(false)

  if (asset.meta_profile_image && !imgError) {
    return (
      <img
        src={asset.meta_profile_image}
        alt={asset.asset_label}
        className="meta-asset-selection__item-avatar"
        onError={() => setImgError(true)}
      />
    )
  }

  return (
    <div className="meta-asset-selection__item-avatar meta-asset-selection__item-avatar--fallback">
      {asset.asset_label.charAt(0).toUpperCase()}
    </div>
  )
}

function AssetGroup({ title, assets, checked, onToggle }) {
  if (!assets.length) return null

  return (
    <div className="meta-asset-selection__group">
      <div className="meta-asset-selection__group-label">{title}</div>
      {assets.map((asset) => (
        <label key={asset.key} className="meta-asset-selection__item">
          <span className="custom-check custom-check--navy">
            <input
              type="checkbox"
              className="custom-check__input"
              checked={checked.has(asset.key)}
              onChange={() => onToggle(asset.key)}
            />
            <span className="custom-check__box" />
          </span>
          <AssetAvatar asset={asset} />
          <div className="meta-asset-selection__item-body">
            <div>{asset.asset_label}</div>
            <div className="meta-asset-selection__item-secondary">
              {asset.asset_class === 'ad_account' && asset.extra_data
                ? `${asset.extra_data.currency} · ${asset.extra_data.account_status}`
                : `${asset.platform} · ${asset.asset_class}`}
            </div>
          </div>
        </label>
      ))}
    </div>
  )
}

export default function AssetSelectionModal() {
  const { t } = useTranslation()
  const { pendingAssetSelection } = useConnectContext()
  const { dispatch } = useConnectDispatchContext()

  const availableAssets = pendingAssetSelection?.availableAssets ?? []
  const taskId = pendingAssetSelection?.taskId

  const [checked, setChecked] = useState(() => new Set(availableAssets.map((a) => a.key)))
  const [submitting, setSubmitting] = useState(false)
  const [confirmError, setConfirmError] = useState(null)

  const grouped = useMemo(
    () => ({
      adAccounts: availableAssets.filter((a) => a.asset_class === 'ad_account'),
      fbProfiles: availableAssets.filter(
        (a) => a.asset_class === 'profile' && a.platform === 'facebook',
      ),
      igProfiles: availableAssets.filter(
        (a) => a.asset_class === 'profile' && a.platform === 'instagram',
      ),
      pixels: availableAssets.filter((a) => a.asset_class === 'pixel'),
    }),
    [availableAssets],
  )

  if (!pendingAssetSelection) return null

  const handleToggle = (key) => {
    setChecked((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const handleCancel = () => {
    dispatch({ type: actions.SET_PENDING_ASSET_SELECTION, payload: null })
  }

  const handleConfirm = async () => {
    setSubmitting(true)
    setConfirmError(null)
    const { status, data, error } = await integrationAssetsApi.confirmFacebookAssetsAsync(taskId, [...checked])
    if (error || status !== 204) {
      setConfirmError(data?.message ?? error?.message ?? t('common.genericError'))
      setSubmitting(false)
      return
    }
    dispatch({ type: actions.SET_PENDING_ASSET_SELECTION, payload: null })
    dispatch({ type: actions.GET_ASSETS, payload: { fromNewConnection: true } })
  }

  const isEmpty = availableAssets.length === 0
  const noneSelected = checked.size === 0

  return (
    <Portal wrapperId="app-portal">
      <Modal
        size="lg"
        hideHeader
        hideCloseBtn
        offWhiteBg
        fullWidth={false}
        mainContent={
          <div className="meta-asset-selection">
            <div className="meta-asset-selection__header">
              <h1>{t('Settings.MetaAccess.AssetSelection.title', 'Select your Meta assets')}</h1>
              <p className="body-txt light larger mb-4">
                {t(
                  'Settings.MetaAccess.AssetSelection.description',
                  'Choose which assets to connect to your account.',
                )}
              </p>
            </div>

            {isEmpty ? (
              <div className="meta-asset-selection__error alert alert-danger">
                {t(
                  'Settings.MetaAccess.AssetSelection.noAssets',
                  'No accessible Meta assets found. Please check your Meta permissions and try again.',
                )}
              </div>
            ) : (
              <>
                <AssetGroup
                  title={t('Settings.MetaAccess.AssetSelection.adAccounts', 'Ad Accounts')}
                  assets={grouped.adAccounts}
                  checked={checked}
                  onToggle={handleToggle}
                />
                <AssetGroup
                  title={t('Settings.MetaAccess.AssetSelection.fbPages', 'Facebook Pages')}
                  assets={grouped.fbProfiles}
                  checked={checked}
                  onToggle={handleToggle}
                />
                <AssetGroup
                  title={t('Settings.MetaAccess.AssetSelection.igAccounts', 'Instagram Accounts')}
                  assets={grouped.igProfiles}
                  checked={checked}
                  onToggle={handleToggle}
                />
                <AssetGroup
                  title={t('Settings.MetaAccess.AssetSelection.pixels', 'Pixels')}
                  assets={grouped.pixels}
                  checked={checked}
                  onToggle={handleToggle}
                />

                {noneSelected && (
                  <p className="meta-asset-selection__empty-warning alert alert-warning">
                    {t(
                      'Settings.MetaAccess.AssetSelection.emptyWarning',
                      'No assets selected. Confirming will save no Meta assets.',
                    )}
                  </p>
                )}
              </>
            )}

            {confirmError && (
              <p className="meta-asset-selection__confirm-error alert alert-danger">
                {confirmError}
              </p>
            )}

            <div className="meta-asset-selection__footer">
              <TransparentBgButton
                text={t('common.cancel', 'Cancel')}
                onClick={handleCancel}
                color="navy"
                disabled={submitting}
              />
              {!isEmpty && (
                <SecondaryButton
                  text={`${t('Settings.MetaAccess.AssetSelection.confirm', 'Confirm')} (${checked.size})`}
                  fullWidth={false}
                  onClick={handleConfirm}
                  disabled={submitting}
                />
              )}
            </div>
          </div>
        }
      />
    </Portal>
  )
}
